<!--
// nuxt-ui/pages/[region]/Draft.vue
-->
<script setup lang="ts">
import type {
  AnyDraftPlayer,
  DraftPlayerFoot,
  DraftPlayerRole,
  DraftPlayerType,
} from "~/src/draft/domain/models/DraftPlayer";
import type { MacroFilters, MacroFilter, DraftYearFilter } from "~/src/draft/domain/models/Filters";
import Banner from "~/nuxt-ui/components/Shared/HeaderBannerTitle.vue";
import DraftPlayers from "~/nuxt-ui/components/pages/region-draft/DraftPlayers.vue";
import SearchField from "~/nuxt-ui/components/Shared/SearchField.vue";
import DraftRoleFilter from "~/nuxt-ui/components/pages/region-draft/DraftRoleFilter.vue";
import DraftPlayerFilters from "~/nuxt-ui/components/pages/region-draft/DraftPlayerFilters.vue";
import draftUiService from "~/src/draft/infrastructure/ui-services/draftUiService";
import draftService from "~/src/draft/domain/services/draftService";
import { initModals } from "flowbite";
import { slugify } from "~/src/Shared/utils";
import { useWebContext } from "~/nuxt-ui/Shared/composables/webContext";
import { useRegions } from "~/nuxt-ui/Shared/composables/regions";
import { useCompetitionStore } from "~/nuxt-ui/stores/competitionStore";
import { useDraftPlayers } from "~/nuxt-ui/Shared/composables/draftPlayers";

type Filters = {
  originDataRole: DraftPlayerType;
  foot: DraftPlayerFoot | null;
  searchTerm: string | null;
  role: DraftPlayerRole | null;
  height: MacroFilter;
  macros?: MacroFilters;
};

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/draft",
    es: "/[region]/draft",
    de: "/[region]/draft",
    fr: "/[region]/draft",
    it: "/[region]/draft",
    pt: "/[region]/draft",
  },
});

definePageMeta({
  middleware: [
    "not-found-world-cup",
    "not-found-nations",
    "not-found-king-of-kings-nations",
    "not-found-germany",
  ],
});

const { getDraftTryoutsData, generateDraftTryoutsOptions } = draftUiService();

const { createMinMaxMacrosFilters, findMacroMinMax, findHeightMinMax } = draftService();

const { isKings, isQueens, contextSiteTitle, webContext } = useWebContext();
const { region } = useCompetitionStore();
const { t } = useI18n();
const { isSpain, isAmericas, isItaly, isBrazil, isFrance } = useRegions();
const { getDraftPlayers } = useDraftPlayers();

const optionsDraft: DraftYearFilter[] = isItaly
  ? [{ id: 3, year: 24, name: "2024" }]
  : isBrazil
    ? [{ id: 4, year: 25, name: "2025" }]
    : isFrance
      ? [{ id: 4, year: 25, name: "2025" }]
      : await generateDraftTryoutsOptions(region?.slug, webContext);

const selectedDraft = ref<number>(!!optionsDraft ? optionsDraft[0].year : 24);

const draftPlayerFiltersKey = ref<number>(0);
const draftPlayersCardsKey = ref<number>(0);

const initialFilters: Filters = {
  originDataRole: "player",
  height: {
    min: 100,
    max: 250,
    from: 100,
    to: 250,
  },
  foot: null,
  searchTerm: null,
  role: null,
};

const filters = reactive<Filters>(JSON.parse(JSON.stringify(initialFilters)));
const playersData = ref<AnyDraftPlayer[] | undefined>([]);

const players = ref<AnyDraftPlayer[] | undefined>([]);
const goalkeepers = ref<AnyDraftPlayer[] | undefined>([]);

const draftData = async (draftNumber: number): Promise<void> => {
  try {
    if (isItaly) {
      players.value = await getDraftPlayers(12, "player");
      goalkeepers.value = await getDraftPlayers(12, "goalkeeper");
    } else if (isBrazil) {
      players.value = await getDraftPlayers(19, "player");
      goalkeepers.value = await getDraftPlayers(19, "goalkeeper");
    } else if (isFrance) {
      players.value = await getDraftPlayers(20, "player");
      goalkeepers.value = await getDraftPlayers(20, "goalkeeper");
    } else {
      players.value = await getDraftTryoutsData(draftNumber, region?.slug, webContext, "player");
      goalkeepers.value = await getDraftTryoutsData(draftNumber, region?.slug, webContext, "goalkeeper");
    }

    playersData.value = filters.originDataRole === "goalkeeper" ? goalkeepers.value : players.value;
  } catch (e) {
    console.error(e);
  }
};

await draftData(selectedDraft.value);

const sortedPlayers = computed(() => {
  if (!playersData.value) return [];

  return playersData.value.sort((a, b) => {
    if (!isItaly && !isBrazil && !isFrance) {
      const draftJerseyA: number = a.draftJersey;
      const draftJerseyB: number = b.draftJersey;
      return draftJerseyA > draftJerseyB ? 1 : draftJerseyA < draftJerseyB ? -1 : 0;
    } else {
      return;
    }
  });
});

const filteredPlayers = computed((): AnyDraftPlayer[] => {
  if (!playersData.value) return [];

  let _players = sortedPlayers.value as AnyDraftPlayer[];

  if (filters.role) {
    _players = _players.filter(p => p.role === filters.role);
  }

  if (filters.searchTerm) {
    _players = _players.filter(p => {
      const { searchTerm } = filters as string;
      return (
        slugify(p.name).includes(slugify(searchTerm)) ||
        (p.draftJersey && p.draftJersey.toString().includes(searchTerm)) ||
        (p.draftJerseyPrefix && p.draftJerseyPrefix.toString().toLowerCase().includes(searchTerm))
      );
    });
  }

  if (filters.originDataRole !== "reserve") {
    if (filters.foot) {
      _players = _players.filter(p => p.foot === filters.foot);
    }

    if (filters.macros) {
      for (const keyMacro in filters.macros) {
        _players = _players.filter(p => {
          const isAboveMin = p.macros[keyMacro] >= filters.macros[keyMacro].min;
          const isBelowMax = p.macros[keyMacro] <= filters.macros[keyMacro].max;
          return isAboveMin && isBelowMax;
        });
      }
    }

    if (filters.height) {
      _players = _players.filter(p => {
        const isAboveMin = p.height >= filters.height.min;
        const isBelowMax = p.height <= filters.height.max;
        return (isAboveMin && isBelowMax) || !p.height;
      });
    }
  }

  return _players;
});

const updateRangeValues = () => {
  if (filteredPlayers.value && filters.originDataRole !== "reserve") {
    const macroFilters = createMinMaxMacrosFilters(filteredPlayers.value);

    if (macroFilters) {
      filters.macros = macroFilters;

      if (playersData.value) {
        for (const macrosKey in filters.macros) {
          const [from, to] = findMacroMinMax(macrosKey, playersData.value);
          filters.macros[macrosKey].from = from;
          filters.macros[macrosKey].to = to;
        }
      }
    }

    const [minHeight, maxHeight] = findHeightMinMax(playersData.value);

    filters.height = {
      min: minHeight,
      max: maxHeight,
      from: minHeight,
      to: maxHeight,
    };
  } else {
    filters.height = {
      min: 0,
      max: 0,
      from: 0,
      to: 0,
    };
  }
};

const resetFilters = () => {
  filters.macros = undefined;
  filters.foot = initialFilters.foot;
  filters.height = initialFilters.height;
  filters.role = initialFilters.role;
  filters.searchTerm = initialFilters.searchTerm;
  updateRangeValues();
  draftPlayerFiltersKey.value++;
  draftPlayersCardsKey.value++;
};

const updateSelectedDraft = async event => {
  const newValue = Number(event.target.value);

  await draftData(newValue);

  resetFilters();
};

watch(
  () => filters.originDataRole,
  newValue => {
    playersData.value = newValue === "goalkeeper" ? goalkeepers.value : players.value;
    resetFilters();
  },
  { immediate: true }
);

onUpdated(() => {
  initModals();
});

useSeoMeta({
  title: t("header.navCompetition.tryouts"),
  ogTitle: t("header.navCompetition.tryouts"),
  description: t("meta.description.draft.page", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.draft.page", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <banner :title="$t('header.navCompetition.tryouts')"></banner>

  <div class="kql-w-container">
    <div class="px-4 lg:pt-16 lg:px-6">
      <div class="flex flex-col sm:flex-row items-center justify-start gap-4 sm:mb-4 lg:mb-7 sm:pt-4">
        <draft-role-filter v-model="filters.originDataRole" @change="updateRangeValues"></draft-role-filter>
        <select
          v-if="optionsDraft.length"
          id="draftValue"
          v-model="selectedDraft"
          @change="e => updateSelectedDraft(e)"
          class="kql-select w-full sm:w-auto text-xs lg:text-sm border-l rounded-lg"
        >
          <option v-for="option in optionsDraft" :key="option.id" :value="option.year">
            {{ option.name }}
          </option>
        </select>
        <div class="order-last mb-4 sm:mb-0 sm:order-auto sm:ml-auto w-full sm:max-w-[364px]">
          <search-field v-model="filters.searchTerm"></search-field>
        </div>
      </div>

      <draft-player-filters
        :key="draftPlayerFiltersKey"
        v-if="filters.macros || (!filters.macros && filters.originDataRole === 'reserve')"
        :player-role="filters.originDataRole"
        v-model:playerFoot="filters.foot"
        v-model:playerPosition="filters.role"
        v-model:macros="filters.macros"
        v-model:height="filters.height"
      ></draft-player-filters>

      <div>
        <button
          class="text-primary border-primary border rounded-lg px-3 py-2 text-xs lg:!my-6 mt-4"
          @click="resetFilters"
        >
          {{ $t("draft.player.filters.clean") }}
        </button>
      </div>
    </div>

    <div class="my-6">
      <draft-players :players="filteredPlayers"></draft-players>
    </div>
  </div>
</template>

<style scoped></style>
